type Row = Record<string, unknown>;

const strip = (s: string) =>
    s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, '').toLowerCase();

// Accent-stripped fragments of "phone" headers (es, pt, fr, de, it, en). A strong
// hint wins over a weak one regardless of column order ("Invoice number" must not
// beat "Phone").
const STRONG_PHONE_HINTS = ['phone', 'mobile', 'whatsapp', 'telefon', 'telefone', 'movil', 'celular', 'handy'];
const WEAK_PHONE_HEADERS = ['tel', 'numero', 'number', 'cell', 'nr'];

/** The contact ("phone") option in a new-audience import's column list. */
export const CONTACT_COLUMN_FAKE_ID = 'fakeContactColumnId01234';

/** A bare number (7-15 digits, any formatting) or an explicit WhatsApp jid.
 *  The backend strips leading zeros and adds no country code, so a local number
 *  (single leading 0, e.g. 07911…) would become an undeliverable id: invalid. */
export const isValidWhatsappId = (v: unknown): boolean => {
    const value = (typeof v === 'number' ? String(v) : typeof v === 'string' ? v : '').trim();
    if (value.includes('@')) return /^\d{7,15}@(c\.us|s\.whatsapp\.net)$/.test(value);
    if (!/^[\d\s()+.-]+$/.test(value)) return false;
    const digits = value.replace(/\D/g, '');
    if (/^0[1-9]/.test(digits)) return false;
    return digits.length >= 7 && digits.length <= 15;
};

/** Header of the column holding WhatsApp numbers. Tiers in order (strong header, weak
 *  header, digit-only column); the first column that actually holds valid ids wins, so a
 *  header like "Mobile app version" cannot shadow a real phone column. A digit-only column
 *  is auto-picked only when it is the single candidate (ambiguous → the user maps it). */
export function findPhoneColumn(headers: string[], rows: Row[]): string | undefined {
    const strongs = headers.filter((h) => STRONG_PHONE_HINTS.some((hint) => strip(h).includes(hint)));
    const weaks = headers.filter((h) => !strongs.includes(h) && WEAK_PHONE_HEADERS.includes(strip(h)));
    const holdsIds = (h: string) => countValidWhatsappIds(rows, h) > 0;
    const named = [...strongs, ...weaks];
    const withIds = named.find(holdsIds);
    if (withIds) return withIds;
    const digitOnly = headers.filter((h) => {
        if (named.includes(h)) return false;
        const values = rows.map((r) => r[h]).filter((v) => v !== undefined && v !== null && v !== '');
        return values.length > 0 && values.every((v) => /^[+\d][\d\s()+.-]*$/.test(String(v)) && isValidWhatsappId(v));
    });
    if (digitOnly.length === 1) return digitOnly[0];
    return named[0];
}

export function countValidWhatsappIds(rows: Row[], column: string | undefined | null): number {
    if (!column) return 0;
    return rows.filter((r) => isValidWhatsappId(r[column])).length;
}

/** Ids of the options a user can map a phone column to: the list's whatsapp-id columns, plus the
 *  new-audience fake one when `includeFake` (it only exists for new audiences; on an existing
 *  board the backend would receive a column id that does not exist). */
export function contactColumnIdsOf(columnsList: Array<{ _id?: string; type?: string }> | undefined, includeFake = false): string[] {
    const ids = (columnsList ?? []).filter((c) => c.type === 'whatsapp-id-cell' && c._id).map((c) => c._id as string);
    return includeFake && !ids.includes(CONTACT_COLUMN_FAKE_ID) ? [...ids, CONTACT_COLUMN_FAKE_ID] : ids;
}

/** Valid ids in the best column the user mapped to a contact (WhatsApp id) option; 0 if none is mapped. */
export function countValidIdsInMappedContactColumn(
    rows: Row[],
    mapping: Record<string, string>,
    contactColumnIds: string[],
): number {
    const columns = Object.keys(mapping).filter((k) => contactColumnIds.includes(mapping[k]));
    return columns.reduce((best, c) => Math.max(best, countValidWhatsappIds(rows, c)), 0);
}

export const NO_VALID_WHATSAPP_ID_MESSAGE =
    'No column is mapped to the WhatsApp number, so every recipient would be skipped. Map your phone-number column to the contact (phone) column and try again.';
